'use client';

import React from 'react';
import { OptionItem } from '../OptionItem';

/**
 * Single Responsibility: Handle true/false question options
 * Open/Closed: Can be extended without modifying base OptionItem
 */

interface TrueFalseOptionsBaseProps {
  question: {
    true_or_false_question?: {
      answers: boolean[];
    };
    metadata_uid: string;
  };
  questionIndex: number;
  selectedOptions: string | string[] | boolean | undefined;
  isQuiz?: boolean;
  correctAnswers?: string | string[] | boolean | null;
  quizData?: any;
  isQuestionSubmitted?: boolean;
}

export function TrueFalseOptionsInteractive({
  question,
  questionIndex,
  selectedOptions,
  handlers,
  isQuiz = false,
  correctAnswers = null,
  isQuestionSubmitted = false,
}: TrueFalseOptionsBaseProps & { handlers: any }) {
  const isChecked = (option: boolean) => {
    // Handle boolean comparison
    if (typeof selectedOptions === 'boolean') {
      return selectedOptions === option;
    }
    // If selectedOptions is a string, convert and compare
    if (typeof selectedOptions === 'string') {
      return (selectedOptions === 'true') === option;
    }
    // If selectedOptions is undefined or not set, return false
    return false;
  };

  const getInputClassName = (option: boolean) => {
    if (!isQuiz || !isQuestionSubmitted) {
      return 'form-check-input outline-focussed cursor-pointer w-5 h-5';
    }

    const isSelected = isChecked(option);
    const correctAnswer =
      typeof correctAnswers === 'boolean'
        ? correctAnswers
        : correctAnswers === 'true'
          ? true
          : correctAnswers === 'false'
            ? false
            : null;

    if (correctAnswer !== null) {
      // User got it wrong - highlight all options
      const isCorrectOption = correctAnswer === option;
      return isCorrectOption
        ? 'form-check-input outline-focussed cursor-pointer w-5 h-5 right-answer'
        : 'form-check-input outline-focussed cursor-pointer w-5 h-5 wrong-answer';
    }

    // User got it right - show selected option as correct
    if (isSelected) {
      return 'form-check-input outline-focussed cursor-pointer w-5 h-5 right-answer';
    }
    return 'form-check-input outline-focussed cursor-pointer w-5 h-5';
  };

  const getFeedbackText = (option: boolean) => {
    if (!isQuiz || !isQuestionSubmitted) return null;

    const isSelected = isChecked(option);
    const correctAnswer =
      typeof correctAnswers === 'boolean'
        ? correctAnswers
        : correctAnswers === 'true'
          ? true
          : correctAnswers === 'false'
            ? false
            : null;

    if (correctAnswer !== null) {
      // User got it wrong - show Correct/Incorrect for all options
      const isCorrectOption = correctAnswer === option;
      return isCorrectOption
        ? { text: 'Correct', className: 'text-green' }
        : { text: 'Incorrect', className: 'text-red' };
    }

    // User got it right - show Correct only for selected option
    if (isSelected) {
      return { text: 'Correct', className: 'text-green' };
    }
    return null;
  };

  return (
    <>
      {[true, false].map((option, index) => {
        const feedback = getFeedbackText(option);
        const shouldShowFeedback = isQuestionSubmitted && feedback != null;

        return (
          <div
            className="form-check flex items-center"
            role="group"
            key={question.metadata_uid + option}
          >
            <span className="relative">
              <input
                type="radio"
                className={getInputClassName(option)}
                name={question.metadata_uid}
                value={option.toString()}
                id={`${question.metadata_uid}-${index}`}
                checked={isChecked(option)}
                onChange={(e) => {
                  // Pass the string value directly - handleTrueFalse will convert it
                  handlers.handleTrueFalse(e.target.value);
                }}
                required
                autoComplete="off"
                disabled={isQuestionSubmitted}
              />
            </span>
            <label
              className={`cs-s-body-regular text-text-default-strong text-capitalize select-none m-0 ${isQuestionSubmitted ? 'cursor-default' : 'cursor-pointer'}`}
              htmlFor={`${question.metadata_uid}-${index}`}
            >
              {option.toString()}
              {shouldShowFeedback && feedback && (
                <>
                  <br />
                  <span className={`cs-caption block ${feedback.className}`}>
                    {feedback.text}
                  </span>
                </>
              )}
            </label>
          </div>
        );
      })}
      {isQuestionSubmitted && correctAnswers !== null && (
        <div className="correct-answer position-relative">
          <p>The correct answer was:</p>
          <p className="!cs-s-body-regular lh-150">
            {typeof correctAnswers === 'boolean'
              ? correctAnswers
                ? 'True'
                : 'False'
              : correctAnswers === 'true'
                ? 'True'
                : correctAnswers === 'false'
                  ? 'False'
                  : ''}
          </p>
        </div>
      )}
    </>
  );
}

export function TrueFalseOptionsReview({
  question,
  questionIndex,
  selectedOptions,
  showCorrectness,
  correctnessChecker,
  correctAnswers = null,
}: TrueFalseOptionsBaseProps & {
  showCorrectness: boolean;
  correctnessChecker?: (option: string | boolean) => boolean | null;
}) {
  const isChecked = (option: boolean) => {
    if (typeof selectedOptions === 'boolean') return selectedOptions === option;
    if (typeof selectedOptions === 'string') return (selectedOptions === 'true') === option;
    return false;
  };

  return (
    <>
      {[true, false].map((option, index) => {
        const isSelected = isChecked(option);
        const correctness = correctnessChecker ? correctnessChecker(option) : null;

        return (
          <OptionItem
            key={question.metadata_uid + option + '-review'}
            option={option}
            isSelected={isSelected}
            questionUid={question.metadata_uid}
            questionIndex={questionIndex}
            optionIndex={index}
            label={option.toString()}
            inputType="radio"
            isDisabled={true}
            correctness={correctness}
            showCorrectness={showCorrectness}
          />
        );
      })}
      {correctAnswers != null && (
        <div className="correct-answer position-relative">
          <p>The correct answer was:</p>
          <p className="!cs-s-body-regular lh-150">
            {typeof correctAnswers === 'boolean'
              ? correctAnswers
                ? 'True'
                : 'False'
              : ''}
          </p>
        </div>
      )}
    </>
  );
}

